import React from "react";
import {Link, RouteComponentProps, withRouter} from "react-router-dom";
import {Menu} from "antd";
import UserData from "../../model/UserData";
import styles from "./styles/GlobalMenu.module.scss";
import { isV2GacEnabled } from "../../utils/v2gac";
import { isV2EventsEnabled } from "../../utils/v2events";
import { isV2TBEnabled } from "../../utils/v2tb";
import { isV2TWEnabled } from "../../utils/v2tw";
import { isV2PlayerEnabled } from "../../utils/v2player";
import { isV2GuildEnabled } from "../../utils/v2guild";
import { isV2TemplatesEnabled } from "../../utils/v2templates";
import { isV2PublicEnabled } from "../../utils/v2public";
import { isV2ComparesEnabled } from "../../utils/v2compares";
import { isV2ModsEnabled } from "../../utils/v2mods";
import { isV2SalvageEnabled } from "../../utils/v2salvage";
import { isV2BronziumEnabled } from "../../utils/v2bronzium";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAnalytics,
    faBoxOpen,
    faConciergeBell,
    faFileChartPie,
    faHeadVr,
    faIdBadge,
    faJournalWhills,
    faMicroscope,
    faPlayCircle,
    faRetweetAlt,
    faRing,
    faSave,
    faSpaceStationMoonAlt,
    faTasks,
    faTasksAlt,
    faUserCog,
    faUserEdit,
    faHistory,
    faCalendarDay,
    faHeartBroken,
    faHelmetBattle,
    faAbacus,
    faSwordsLaser,
    faStarfighter,
    faInfoCircle,
    faSearch,
    faTicketAlt,
    faMapMarkedAlt,
    faShareAlt,
    faStopwatch,
    faFarm,
    faEngineWarning,
    faUserAstronaut,
    faMap,
    faHome,
    faDatabase,
    faHeadSideBrain,
    faHatWizard,
    faShieldAlt,
    faRobotAstromech,
    faPresentationScreen,
    faHammerCrash,
    faBoxDollar,
    faUserPolice,
    faPersonDigging,
    faRulerVertical,
    faBarsProgress,
    faNewspaper,
    faSatelliteDish,
    faScaleBalanced,
    faGem,
    faRecycle
} from "@fortawesome/pro-duotone-svg-icons";
import {
    faGalacticRepublic, faGuilded, faOldRepublic, faPhoenixSquadron, faRebel, faYoutubeSquare
} from "@fortawesome/free-brands-svg-icons";
import SubMenu from "antd/lib/menu/SubMenu";

interface IProps extends RouteComponentProps<any> {
    showEventsLink?: boolean;
    user?: UserData;
    isCollapsed: boolean;
    isMobile: boolean;
    /**
     * Logged-out presentation (the public landing + public tabs): every section renders VISIBLE but
     * DISABLED except the HOLONET group — Home, Players, Guilds and Units — the site's whole map stays
     * browsable as a preview,
     * and nothing navigates anywhere that would just bounce to /login. Distinct from simply having no
     * user: the guild block below normally hides without a player, but in public mode it must SHOW,
     * disabled, or the menu shrinks to nothing exactly when it is meant to advertise what exists.
     */
    publicMode?: boolean;
}

/**
 * The HoloNet SubMenu's key. It is deliberately NOT a route: nothing is served at /holonet, and the
 * section's children keep their own URLs. It only has to be stable and collision-free, because
 * `defaultOpenKeys` below names it directly.
 */
const HOLONET_KEY = "/holonet";

/**
 * The routes that live under HoloNet, in the normalised form `getPathName` returns (so a deep-linked
 * profile counts as being in the section). Used to decide whether to open it on mobile.
 */
const HOLONET_ROUTES = ["/", "/players/v2", "/guilds/v2", "/units/v2", "/gac/public"];

/**
 * The LEGACY SubMenu's key. Like HOLONET_KEY it is deliberately NOT a route — nothing is served at
 * /legacy, and every child keeps the URL it has always had, so bookmarks and deep links are untouched.
 * The whole point of the wrapper is that the old tools can later be dropped as one block.
 */
const LEGACY_KEY = "/legacy";

/**
 * The V2 tabs that now sit at ROOT level (owner, 2026-08-27: bring the new apps to the forefront and
 * group everything legacy under one heading).
 *
 * 🔴 Their first path segment collides with a legacy section on purpose — `/gac/v2` shares `gac` with the
 * old GAC tool, `/tb/v2` with the old TB one, `/mods/v2` with the legacy Mods section. `openKeys()`
 * derives a section from that segment, so these have to be excluded there or arriving at a new tab expands
 * the old tool you moved away from.
 */
const V2_ROOT_ROUTES = [
    "/gac/v2",
    "/compares",
    "/mods/v2",
    "/player/datacrons/v2",
    "/player/events/v2",
    "/squads/v2",
    "/guild/overview/v2",
    "/templates/v2",
    "/guild/events/v2",
    "/tw/v2",
    "/tb/v2",
    "/player/salvage/v2",
    "/player/bronzium/v2",
];

/** The legacy sections that are SubMenus inside LEGACY_KEY — keyed by their first path segment. */
const LEGACY_SECTION_KEYS = ["/mods", "/player", "/gac", "/conquest", "/tb", "/tw", "/guild", "/template"];

/** Legacy leaves that hang directly off LEGACY_KEY with no section of their own. */
const LEGACY_ITEM_ROUTES = ["/squads"];

/**
 * Legacy routes whose first path segment does NOT name their section. Only one so far: Sharing lives
 * under Player but is served at /settings/share, so the segment is `settings` and nothing matched it —
 * the section stayed closed. Harmless while Player was at root; inside LEGACY it would leave the page
 * you are on two collapsed levels deep.
 */
const LEGACY_ROUTE_SECTIONS: { [route: string]: string } = { "/settings/share": "/player" };

class GlobalMenu extends React.Component<IProps> {
    getPathName() {
        if ((this.props.location.pathname.split("/")[1] === "guild") && (this.props.location.pathname.split("/")[2] === "compare") && (this.props.location.pathname.split("/")[3] === "scouting")) {
            return "/tw/search";
        } else if ((this.props.location.pathname.split("/")[2] === "compare") || (this.props.location.pathname.split("/")[2] === "members" && !!this.props.location.pathname.split("/")[3]?.match(/^-?\d+$/))) {
            return "/guild/search";
        } else if (this.props.location.pathname.startsWith("/tb/planning")) {
            return "/tb/planning";
        } else if (this.props.location.pathname.startsWith("/gac/view")) {
            return "/gac/history";
        }
        // V2-Compares carries its selected tab in the URL (/compares/player|guild|gac) so a deep link
        // lands on the right one — same normalisation reason as the HoloNet routes below: `selectedKeys`
        // matches EXACTLY, so without this the menu item unhighlights the moment you pick a tab.
        if (this.props.location.pathname.startsWith("/compares")) return "/compares";
        // HoloNet's routes carry an id (/players/v2/:allyCode, /guilds/v2/:guildId, /units/v2/:baseId),
        // and `selectedKeys` matches a key EXACTLY — so before this a deep-linked profile left the menu
        // with nothing highlighted, as though you were nowhere. Collapse each to its tab's own key, the
        // same normalisation the four branches above already do for their sections.
        const holonet = ["/players/v2", "/guilds/v2", "/units/v2", "/gac/public"];
        const base = holonet.find((k) => this.props.location.pathname.startsWith(k));
        if (base) return base;
        return this.props.location.pathname;
    }

    /**
     * Which SubMenus start open. Three cases, and the first two are guards rather than features:
     *
     *  - 🔴 A HOLONET or ROOT-LEVEL V2 route must NOT open the section its first segment happens to name.
     *    `/gac/public` and `/gac/v2` both start with `gac`, which keys the OLD GAC section inside Legacy —
     *    so without this, arriving at either expands the very tool the tab exists to replace (and, for a
     *    logged-out visitor, a `disabled` one). Nothing errors; it just points at the wrong place.
     *
     *  - A LEGACY route needs TWO keys, not one: its own section AND the LEGACY wrapper above it. antd
     *    honours a nested openKey only if the parent is open as well, so with the section key alone the
     *    page you are on would sit inside a collapsed heading with nothing looking selected.
     *
     *  - Anything else keeps the original behaviour: the section whose key is the first path segment.
     *    (Since Mods moved into Legacy on 2026-08-31, no root-level SubMenu is left outside Legacy.)
     *
     * ⚠️ Read ONCE per mount, and that is fine: every page renders its own BasicLayout, so this menu
     * remounts on navigation and re-derives — the same mechanic BasicLayout uses to close the mobile nav.
     */
    openKeys(): string[] {
        const path = this.getPathName();
        const inHolonet = HOLONET_ROUTES.includes(path);
        // Desktop: HoloNet always — expanded when you arrive at the site, which is the point of
        // gathering the public surfaces there (owner, 2026-08-24). Mobile: only when you are actually
        // IN it, because there the menu is a full-screen overlay and an expanded section pushes the
        // rest below the fold.
        const holonetKeys = (!this.props.isMobile || inHolonet) ? [HOLONET_KEY] : [];

        if (inHolonet || V2_ROOT_ROUTES.includes(path)) return holonetKeys;

        const section = LEGACY_ROUTE_SECTIONS[path] ?? "/" + path.split("/")[1];
        if (LEGACY_SECTION_KEYS.includes(section)) return [section, LEGACY_KEY, ...holonetKeys];
        if (LEGACY_ITEM_ROUTES.includes(path)) return [LEGACY_KEY, ...holonetKeys];
        return [section, ...holonetKeys];
    }

    render() {
        let showFullEventsLink = this.props.showEventsLink && this.props.user && this.props.user.currentPlayer;
        let isReadOnly = !this.props.user || this.props.user.readOnly;
        let allyCode = "";
        if (this.props.user && this.props.user.currentPlayer) allyCode = this.props.user.currentPlayer.allyCode;

        const inGuild = this.props.user && this.props.user.currentPlayer && this.props.user.currentPlayer.guildName !== null;
        // `ADMIN` in the user's apiRights. Used ONLY to decide whether an admin-only tab is shown — see the
        // note on the V2-Content item for why this is not, and cannot be, the real gate.
        const isAdmin = !!this.props.user && this.props.user.isAdmin;
        // Public (logged-out) mode: everything below except the HoloNet group gets disabled.
        const locked = !!this.props.publicMode;
        /*
            TB, TW and the guild tools are guild-scoped: they have always been hidden outright for a
            player with no guild, and shown DISABLED in public mode (which exists to advertise the map).
            The four V2 tabs that moved up to root level keep exactly that rule — promoting a tab must
            not change who can see it, only where it sits.
        */
        const guildScoped = inGuild || locked;


        return (<Menu
                theme="light"
                mode="inline"
                selectedKeys={[this.getPathName()]}
                className={`${styles.menu} ${this.props.isCollapsed ? (this.props.isMobile ? '' : styles.collapsed) : styles.expanded} ${this.props.isMobile ? styles.mobile : ''}`}
                defaultOpenKeys={this.openKeys()}
            >
                <Menu.ItemGroup key="dashboard" className={styles.category}>
                    {/*
                        HOLONET — every LOGIN-FREE surface lives here (owner, 2026-08-24). Home, the public
                        player and guild profiles, the unit catalog and the public GAC page: the parts of
                        the site a visitor can use without an account, gathered under one heading instead
                        of scattered at root.

                        🔴 NOT `disabled={locked}`: this whole section is what public mode exists to show.
                        Everything else in the menu greys out for a logged-out visitor; these must not.

                        The child ROUTES are unchanged — "/", "/players/v2", "/guilds/v2" — so bookmarks,
                        the landing's own links and `selectedKeys` all keep working. Only the grouping and
                        the labels moved; "V2-" was an implementation detail, not a name for a visitor.
                    */}
                    <SubMenu key={HOLONET_KEY} icon={<FontAwesomeIcon icon={faSatelliteDish} className={styles.icon}/>} title="HoloNet" className={styles.category}>
                        <Menu.Item key="/" icon={<FontAwesomeIcon icon={faHome} className={styles.icon}/>} className={`${styles.item}`}>
                            <Link to="/" className={styles.link}>Home</Link>
                        </Menu.Item>
                        {isV2PublicEnabled() && (
                            <Menu.Item key="/players/v2" icon={<FontAwesomeIcon icon={faIdBadge} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/players/v2" className={styles.link}>Players</Link>
                            </Menu.Item>
                        )}
                        {isV2PublicEnabled() && (
                            <Menu.Item key="/guilds/v2" icon={<FontAwesomeIcon icon={faGuilded} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/guilds/v2" className={styles.link}>Guilds</Link>
                            </Menu.Item>
                        )}
                        {/* The unit catalog. Same icon as Guild → Units on purpose: same noun, different
                            scope, and this menu already reuses an icon where the concept repeats. */}
                        {isV2PublicEnabled() && (
                            <Menu.Item key="/units/v2" icon={<FontAwesomeIcon icon={faHelmetBattle} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/units/v2" className={styles.link}>Units</Link>
                            </Menu.Item>
                        )}
                        {/* Grand Arena counters and metas — game data, so it is login-free and belongs here
                            rather than in the signed-in GAC section below. Same shield icon as that section
                            on purpose: same subject, different scope, and this menu already reuses an icon
                            where the concept repeats (Units does exactly that). */}
                        {isV2PublicEnabled() && (
                            <Menu.Item key="/gac/public" icon={<FontAwesomeIcon icon={faShieldAlt} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/gac/public" className={styles.link}>GAC</Link>
                            </Menu.Item>
                        )}
                    </SubMenu>

                    {/* The signed-in dashboard moved off "/" when the public landing took the root URL
                        (landing redesign). Same disabled-until-login rule as every other gated tab.

                        ⚠️ Not named in the owner's 2026-08-27 tab order, which starts at HoloNet — kept
                        deliberately, because the same instruction repointed THIS page's quick actions at
                        the V2 apps, so it is current work, not legacy. */}
                    <Menu.Item key="/dashboard" disabled={locked} icon={<FontAwesomeIcon icon={faPresentationScreen} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                        <Link to="/dashboard" className={styles.link}>Dashboard</Link>
                    </Menu.Item>

                    {/*
                        THE V2 TABS — every new app, at root level, in the owner's order (2026-08-27).

                        🔴 Each one keeps the ROUTE it already had, including the ones whose URL still
                        reads like a legacy sub-page (`/player/events/v2`, `/guild/overview/v2`). Only the
                        placement and the label moved; the paths are load-bearing — App.tsx, bookmarks,
                        `selectedKeys`, and V2_ROOT_ROUTES above all name them.

                        ⚠️ Every `isV2*Enabled()` is TRUE ONLY on development.hotutils.com and localhost,
                        and each route in App.tsx carries the same gate. So on hotutils.com this whole
                        block renders nothing and the menu is HoloNet / Dashboard / Video Content /
                        Legacy. The gate stays per-tab rather than one wrapper because they will graduate
                        to prod one at a time.
                    */}
                    {isV2GacEnabled() && (
                        <Menu.Item key="/gac/v2" disabled={locked} icon={<FontAwesomeIcon icon={faShieldAlt} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/gac/v2" className={styles.link}>V2-GAC</Link>
                        </Menu.Item>
                    )}
                    {/* V2-Compares sits next to V2-GAC because two of its three tabs ARE the player and
                        GAC compare pages, and the GAC one is the very page the V2-GAC tab will also show
                        (compare-system.md §14.1 — one implementation, two mounts). Its own key is
                        "/compares"; the link carries no tab, so the page picks the first one this viewer
                        can use (getPathName() collapses /compares/* back to this key). */}
                    {isV2ComparesEnabled() && (
                        <Menu.Item key="/compares" disabled={locked} icon={<FontAwesomeIcon icon={faScaleBalanced} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/compares" className={styles.link}>V2-Compares</Link>
                        </Menu.Item>
                    )}
                    {isV2PlayerEnabled() && (
                        <Menu.Item key="/player/datacrons/v2" disabled={locked} icon={<FontAwesomeIcon icon={faBoxDollar} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/player/datacrons/v2" className={styles.link}>V2-Datacrons</Link>
                        </Menu.Item>
                    )}
                    {/* Personal Events, moved out of the guild Events app 2026-08-15 — embeds
                        HotUtilsV2-Player at /events (same app as V2-Datacrons, different page). Labelled
                        "V2-Player Events" now it has left the Player section: standing alone at root, a
                        bare "V2-Events" was indistinguishable from the guild one further down. */}
                    {isV2PlayerEnabled() && (
                        <Menu.Item key="/player/events/v2" disabled={locked} icon={<FontAwesomeIcon icon={faCalendarDay} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/player/events/v2" className={styles.link}>V2-Player Events</Link>
                        </Menu.Item>
                    )}
                    {/* V2-Mods sits with the other two HotUtilsV2-Player tabs above — same app, a third page
                        inside it (mod-system-v2.md §4.6) — and it is ADDITIVE: the legacy "Mods" SubMenu
                        (inside Legacy, below) keeps all 14 of its routes for the whole migration. Hence a
                        distinct icon and the "/mods/v2" key, which V2_ROOT_ROUTES excludes from openKeys()
                        so arriving here does not expand the old suite. */}
                    {isV2ModsEnabled() && (
                        <Menu.Item key="/mods/v2" disabled={locked} icon={<FontAwesomeIcon icon={faGem} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/mods/v2" className={styles.link}>V2-Mods</Link>
                        </Menu.Item>
                    )}
                    {/* V2-Salvage — the fourth HotUtilsV2-Player page, beside the three above. ADDITIVE: the
                        legacy "Salvage" item in the Player section further down keeps its route. A distinct
                        icon (faRecycle vs the legacy faHammerCrash) because both are visible at once, and the
                        "/player/salvage/v2" key is in V2_ROOT_ROUTES so arriving here does not expand the old
                        Player section. 🔴 The page scraps gear irreversibly; isV2SalvageEnabled() keeps it to
                        development until a live exchange has been watched. */}
                    {isV2SalvageEnabled() && (
                        <Menu.Item key="/player/salvage/v2" disabled={locked} icon={<FontAwesomeIcon icon={faRecycle} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/player/salvage/v2" className={styles.link}>V2-Salvage</Link>
                        </Menu.Item>
                    )}
                    {/* V2-Bronzium — the fifth HotUtilsV2-Player page (HotUtilsV2 docs/design/bronzium-opener.md).
                        ADDITIVE: the legacy "Bronzium" item in the Player section further down keeps its route. A
                        distinct icon (faBoxOpen) because both are visible at once, and the "/player/bronzium/v2" key
                        is in V2_ROOT_ROUTES so arriving here does not expand the old Player section. 🔴 The page
                        spends Ally Points on the live account; isV2BronziumEnabled() keeps it to development until
                        a live run has been watched. */}
                    {isV2BronziumEnabled() && (
                        <Menu.Item key="/player/bronzium/v2" disabled={locked} icon={<FontAwesomeIcon icon={faBoxOpen} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/player/bronzium/v2" className={styles.link}>V2-Bronzium</Link>
                        </Menu.Item>
                    )}
                    {/* V2-Squads and V2-Templates embed the SAME app (HotUtilsV2-Templates) at different
                        paths — squads are templates with a squad-shaped presentation, not a separate
                        product — which is why they share one gate. */}
                    {isV2TemplatesEnabled() && (
                        <Menu.Item key="/squads/v2" disabled={locked} icon={<FontAwesomeIcon icon={faPhoenixSquadron} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/squads/v2" className={styles.link}>V2-Squads</Link>
                        </Menu.Item>
                    )}
                    {/* "V2-Guild", not "V2-Overview": at root level the old label named a page inside a
                        section that no longer contains it. Route unchanged. */}
                    {guildScoped && isV2GuildEnabled() && (
                        <Menu.Item key="/guild/overview/v2" disabled={locked} icon={<FontAwesomeIcon icon={faGalacticRepublic} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/guild/overview/v2" className={styles.link}>V2-Guild</Link>
                        </Menu.Item>
                    )}
                    {isV2TemplatesEnabled() && (
                        <Menu.Item key="/templates/v2" disabled={locked} icon={<FontAwesomeIcon icon={faTasksAlt} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/templates/v2" className={styles.link}>V2-Templates</Link>
                        </Menu.Item>
                    )}
                    {guildScoped && isV2EventsEnabled() && (
                        <Menu.Item key="/guild/events/v2" disabled={locked} icon={<FontAwesomeIcon icon={faCalendarDay} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/guild/events/v2" className={styles.link}>V2-Guild Events</Link>
                        </Menu.Item>
                    )}
                    {guildScoped && isV2TWEnabled() && (
                        <Menu.Item key="/tw/v2" disabled={locked} icon={<FontAwesomeIcon icon={faStarfighter} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/tw/v2" className={styles.link}>V2-TW</Link>
                        </Menu.Item>
                    )}
                    {guildScoped && isV2TBEnabled() && (
                        <Menu.Item key="/tb/v2" disabled={locked} icon={<FontAwesomeIcon icon={faSpaceStationMoonAlt} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                            <Link to="/tb/v2" className={styles.link}>V2-TB</Link>
                        </Menu.Item>
                    )}

                    <Menu.Item key="/contentcreators" disabled={locked} icon={<FontAwesomeIcon icon={faYoutubeSquare} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
                        <Link to="/contentcreators" className={styles.link}>Video Content</Link>
                    </Menu.Item>
                    {window.location.host === "localhost:3010" && <Menu.Item key={"/developer"} disabled={locked} icon={<FontAwesomeIcon icon={faEngineWarning} className={styles.icon}/>} className={`${styles.item} ${styles.pad0}`}>
						<Link to={"/developer"} className={styles.link}>Developer Tools</Link>
					</Menu.Item>}

                    {/*
                        LEGACY — the old tools, gathered so they can be dropped as one block once their V2
                        replacements land (owner, 2026-08-27). Every child keeps its own route: this is a
                        menu grouping, not a redirect, so nothing that links to /gac/current or
                        /guild/members changes.

                        🔴 The wrapper is deliberately NOT `disabled={locked}` while each section inside it
                        still is. antd will not expand a disabled SubMenu, so disabling the wrapper would
                        shrink the logged-out preview from nine greyed section headings to one — and
                        advertising the whole map is exactly what public mode is for. The children's own
                        `disabled` still stops a visitor navigating anywhere that would bounce to /login.

                        ⚠️ The nested SubMenus keep their ORIGINAL keys (`/player`, `/gac`, …) because
                        `openKeys()` derives them from the first path segment. Renaming one silently stops
                        the section opening on the page you are on.
                    */}
                    <SubMenu key={LEGACY_KEY} icon={<FontAwesomeIcon icon={faHistory} className={styles.icon}/>} title="Legacy" className={styles.category}>
                        <Menu.Item key="/squads" disabled={locked} icon={<FontAwesomeIcon icon={faPhoenixSquadron} className={styles.icon}/>} className={styles.item}>
                            <Link to="/squads" className={styles.link}>Squads</Link>
                        </Menu.Item>

                        {/* MODS — the legacy mods suite, moved in from root level now V2-Mods exists
                            (owner, 2026-08-31; it had stayed at root while it had no V2 replacement).
                            Keeps its "/mods" key and all 14 routes — LEGACY_SECTION_KEYS names it so
                            openKeys() opens both this section and the Legacy wrapper. */}
                        <SubMenu disabled={locked} key="/mods" icon={<FontAwesomeIcon icon={faAbacus} className={styles.icon}/>} title="Mods" className={styles.category}>
                            <Menu.Item key="/mods/loadouts" icon={<FontAwesomeIcon icon={faTasksAlt} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/loadouts" className={styles.link}>Loadouts</Link>
                            </Menu.Item>
                            <Menu.Item key="/mods/management" icon={<FontAwesomeIcon icon={faTasks} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/management" className={styles.link}>Management</Link>
                            </Menu.Item>
                            <Menu.Item key="/mods/upgrade" icon={<FontAwesomeIcon icon={faAbacus} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/upgrade" className={styles.link}>Upgrade (Beta)</Link>
                            </Menu.Item>
                            <Menu.Item key="/mods/optimizer"
                                       icon={<FontAwesomeIcon icon={faMicroscope} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/optimizer" className={styles.link}>GI's Optimizer</Link>
                            </Menu.Item>
                            <Menu.Item key="/mods/wizard"
                                       icon={<FontAwesomeIcon icon={faHatWizard} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/wizard" className={styles.link}>Wizard</Link>
                            </Menu.Item>

                            <Menu.Item key="/mods/playground" icon={<FontAwesomeIcon icon={faRing} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/playground" className={styles.link}>Playground</Link>
                            </Menu.Item>
                            <Menu.Item key="/mods/analysis"
                                       icon={<FontAwesomeIcon icon={faAnalytics} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/analysis" className={styles.link}>Analysis</Link>
                            </Menu.Item>

                            <Menu.Item key="/mods/depth"
                                       icon={<FontAwesomeIcon icon={faRulerVertical} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/depth" className={styles.link}>Depth</Link>
                            </Menu.Item>

                            <Menu.Item key="/mods/maintenance"
                                       icon={<FontAwesomeIcon icon={faPersonDigging} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/maintenance" className={styles.link}>Maintenance</Link>
                            </Menu.Item>

                            <Menu.Item key="/mods/swap" icon={<FontAwesomeIcon icon={faRetweetAlt} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/swap" className={styles.link}>Swap</Link>
                            </Menu.Item>

                            <Menu.Item key="/mods/services"
                                       icon={<FontAwesomeIcon icon={faConciergeBell} className={styles.icon}/>} className={styles.item}>
                                <Link to="/mods/services" className={styles.link}>Services</Link>
                            </Menu.Item>
                        </SubMenu>

                        <SubMenu disabled={locked} key="/player" icon={<FontAwesomeIcon icon={faUserAstronaut} className={styles.icon}/>} title="Player" className={styles.category}>
                            <Menu.Item key="/player/profile" icon={<FontAwesomeIcon icon={faIdBadge} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/player/profile" className={styles.link}>Profile</Link>
                            </Menu.Item>
                            <Menu.Item key="/player/bronzium" icon={<FontAwesomeIcon icon={faBoxOpen} className={styles.icon}/>} className={`${styles.item} `}>
                                <Link to="/player/bronzium" className={styles.link}>Bronziums</Link>
                            </Menu.Item>
                            <Menu.Item key={"/player/farming/" + allyCode} icon={<FontAwesomeIcon icon={faFarm} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to={"/player/farming/" + allyCode} className={styles.link}>Farming</Link>
                            </Menu.Item>
                            <Menu.Item key="/player/salvage/" icon={<FontAwesomeIcon icon={faHammerCrash} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/player/salvage/" className={styles.link}>Salvage</Link>
                            </Menu.Item>
                            <Menu.Item key="/player/datacrons/" icon={<FontAwesomeIcon icon={faBoxDollar} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to="/player/datacrons/" className={styles.link}>Datacrons</Link>
                            </Menu.Item>
                            <Menu.Item key={"/player/events"} icon={<FontAwesomeIcon icon={faCalendarDay} className={styles.icon}/>} className={`${styles.item}`}>
                                <Link to={"/player/events"} className={styles.link}>Events</Link>
                            </Menu.Item>
                            {!isReadOnly && <Menu.Item key="/settings/share" icon={<FontAwesomeIcon icon={faShareAlt} className={styles.icon}/>} className={styles.item}>
								<Link to="/settings/share" className={styles.link}>Sharing</Link>
							</Menu.Item>}

                            {window.location.host !== "hotutils.com" && <Menu.Item key="/player/strangetracker" icon={<FontAwesomeIcon icon={faFarm} className={styles.icon}/>} className={styles.item}>
								<Link to="/player/strangetracker" className={styles.link}>Tracker For Strange</Link>
							</Menu.Item>}
                        </SubMenu>

                        <SubMenu disabled={locked} key="/gac" icon={<FontAwesomeIcon icon={faShieldAlt} className={styles.icon}/>} title="GAC" className={styles.category}>
                            <Menu.Item key="/gac/current" icon={<FontAwesomeIcon icon={faFileChartPie} className={styles.icon}/>} className={styles.item}>
                                <Link to="/gac/current" className={styles.link}>Current</Link>
                            </Menu.Item>
                            <Menu.Item key="/gac/compare" icon={<FontAwesomeIcon icon={faFileChartPie} className={styles.icon}/>} className={styles.item}>
                                <Link to="/gac/compare" className={styles.link}>GAC Compare</Link>
                            </Menu.Item>
                            <Menu.Item key="/gac/history" icon={<FontAwesomeIcon icon={faHistory} className={styles.icon}/>} className={styles.item}>
                                <Link to="/gac/history" className={styles.link}>History</Link>
                            </Menu.Item>
                            <Menu.Item key="/gac/planning" icon={<FontAwesomeIcon icon={faMap} className={styles.icon}/>} className={styles.item}>
                                <Link to="/gac/planning" className={styles.link}>Planning</Link>
                            </Menu.Item>
                        </SubMenu>

                        <SubMenu disabled={locked} key="/conquest" icon={<FontAwesomeIcon icon={faSwordsLaser} className={styles.icon}/>} title="Conquest" className={styles.category}>
                            <Menu.Item key="/conquest/data_disks"
                                       icon={<FontAwesomeIcon icon={faSave} className={styles.icon}/>} className={styles.item}>
                                <Link to="/conquest/data_disks" className={styles.link}>Data Disks</Link>
                            </Menu.Item>
                            <Menu.Item key="/conquest/status"
                                       icon={<FontAwesomeIcon icon={faRetweetAlt} className={styles.icon}/>} className={styles.item}>
                                <Link to="/conquest/status" className={styles.link}>Status</Link>
                            </Menu.Item>
                            {/* V2-Conquest — the Status page rebuilt in HotUtilsV2-Player. Gated by the same
                                isV2PlayerEnabled() as its route in App.tsx; gating one and not the other puts
                                a tab where it cannot work. */}
                            {isV2PlayerEnabled() && (
                                <Menu.Item key="/conquest/v2"
                                           icon={<FontAwesomeIcon icon={faRetweetAlt} className={styles.icon}/>} className={styles.item}>
                                    <Link to="/conquest/v2" className={styles.link}>V2-Status</Link>
                                </Menu.Item>
                            )}
                            {/* 🔴 The V2 Data Disks page is reachable ONLY from here. The Player app selects
                                its page by the path the iframe loads and nothing inside it navigates, so the
                                planner tab cannot link across — without this item the page exists and no
                                player can get to it. */}
                            {isV2PlayerEnabled() && (
                                <Menu.Item key="/conquest/v2-disks"
                                           icon={<FontAwesomeIcon icon={faSave} className={styles.icon}/>} className={styles.item}>
                                    <Link to="/conquest/v2-disks" className={styles.link}>V2-Data Disks</Link>
                                </Menu.Item>
                            )}
                            {/* The curated-content editor — the advice EVERY player's planner reads, so the
                                tab is shown only to admins.

                                🔴 This is a VISIBILITY gate and nothing more. `apiRights` is client-side
                                state from the legacy API, so neither the embedded app nor its BFF can verify
                                it. The gate that actually protects the content is the Player BFF's
                                server-side allow-list, checked against verified parent-token claims on every
                                read and write. Hiding the tab is tidiness; the allow-list is the security. */}
                            {isV2PlayerEnabled() && isAdmin && (
                                <Menu.Item key="/conquest/v2-content"
                                           icon={<FontAwesomeIcon icon={faUserEdit} className={styles.icon}/>} className={styles.item}>
                                    <Link to="/conquest/v2-content" className={styles.link}>V2-Content</Link>
                                </Menu.Item>
                            )}
                        </SubMenu>

                        {/* Guild-scoped: hidden outright for a player with no guild, shown disabled in
                            public mode. Unchanged from when these sat at root — same rule as the V2
                            guild tabs above (`guildScoped`). */}
                        {(inGuild || locked) && <React.Fragment>
							<SubMenu disabled={locked} key="/tb" icon={<FontAwesomeIcon icon={faSpaceStationMoonAlt} className={styles.icon}/>} title="TB" className={styles.category}>
								<Menu.Item key="/tb/status" icon={<FontAwesomeIcon icon={faInfoCircle} className={styles.icon}/>} className={styles.item}>
									<Link to="/tb/status" className={styles.link}>Status</Link>
								</Menu.Item>
								<Menu.Item key="/tb/players" icon={<FontAwesomeIcon icon={faHeadVr} className={styles.icon}/>} className={styles.item}>
									<Link to="/tb/players" className={styles.link}>Players</Link>
								</Menu.Item>
								<Menu.Item key="/tb/planning" icon={<FontAwesomeIcon icon={faMapMarkedAlt} className={styles.icon}/>} className={styles.item}>
									<Link to="/tb/planning" className={styles.link}>Planning</Link>
								</Menu.Item>
								<Menu.Item key="/tb/command" icon={<FontAwesomeIcon icon={faOldRepublic} className={styles.icon}/>} className={styles.item}>
									<Link to="/tb/command" className={styles.link}>Command</Link>
								</Menu.Item>
								<Menu.Item key="/tb/data" icon={<FontAwesomeIcon icon={faDatabase} className={styles.icon}/>} className={styles.item}>
									<Link to="/tb/data" className={styles.link}>Data</Link>
								</Menu.Item>


								<Menu.Item key="/tb/strategy" icon={<FontAwesomeIcon icon={faHeadSideBrain} className={styles.icon}/>} className={styles.item}>
									<Link to="/tb/strategy" className={styles.link}>Manage</Link>
								</Menu.Item>
							</SubMenu>

							<SubMenu disabled={locked} key="/tw" title="TW" icon={<FontAwesomeIcon icon={faStarfighter} className={styles.icon}/>} className={styles.category}>
								<Menu.Item key="/tw/current"
										   icon={<FontAwesomeIcon icon={faJournalWhills} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/current" className={styles.link}>Current</Link>
								</Menu.Item>
								<Menu.Item key="/tw/search" icon={<FontAwesomeIcon icon={faSearch} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/search" className={styles.link}>Scouting</Link>
								</Menu.Item>
								<Menu.Item key="/tw/history" icon={<FontAwesomeIcon icon={faHistory} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/history" className={styles.link}>History</Link>
								</Menu.Item>
								<Menu.Item key="/tw/scoreboard" icon={<FontAwesomeIcon icon={faPresentationScreen} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/scoreboard" className={styles.link}>Scoreboard</Link>
								</Menu.Item>
								<Menu.Item key="/tw/command" icon={<FontAwesomeIcon icon={faOldRepublic} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/command" className={styles.link}>Command</Link>
								</Menu.Item>
								<Menu.Item key="/tw/planning" icon={<FontAwesomeIcon icon={faRobotAstromech} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/planning" className={styles.link}>Planning</Link>
								</Menu.Item>
								<Menu.Item key="/tw/reporting" icon={<FontAwesomeIcon icon={faNewspaper} className={styles.icon}/>} className={styles.item}>
									<Link to="/tw/reporting" className={styles.link}>Reporting</Link>
								</Menu.Item>
							</SubMenu>

							<SubMenu disabled={locked} key="/guild" title="Guild" icon={<FontAwesomeIcon icon={faGalacticRepublic} className={styles.icon}/>} className={styles.category}>
								<Menu.Item key="/guild/members" icon={<FontAwesomeIcon icon={faGuilded} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/members" className={styles.link}>Overview</Link>
								</Menu.Item>
								<Menu.Item key="/guild/units" icon={<FontAwesomeIcon icon={faHelmetBattle} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/units" className={styles.link}>Units</Link>
								</Menu.Item>
								<Menu.Item key="/guild/tickets" icon={<FontAwesomeIcon icon={faTicketAlt} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/tickets" className={styles.link}>Tickets</Link>
								</Menu.Item>
								<Menu.Item key="/guild/events" icon={<FontAwesomeIcon icon={faCalendarDay} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/events" className={styles.link}>Events</Link>
								</Menu.Item>
								<Menu.Item key="/guild/violations" icon={<FontAwesomeIcon icon={faHeartBroken} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/violations" className={styles.link}>Violations</Link>
								</Menu.Item>
								<Menu.Item key="/guild/search" icon={<FontAwesomeIcon icon={faSearch} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/search" className={styles.link}>Search</Link>
								</Menu.Item>
                                {window.location.host !== "hotutils.com" && <Menu.Item key="/guild/watch" icon={<FontAwesomeIcon icon={faStopwatch} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/watch" className={styles.link}>Watch</Link>
								</Menu.Item>}
								<Menu.Item key="/guild/farming" icon={<FontAwesomeIcon icon={faFarm} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/farming" className={styles.link}>Farming</Link>
								</Menu.Item>
								<Menu.Item key="/guild/glProgress" icon={<FontAwesomeIcon icon={faBarsProgress} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/glProgress" className={styles.link}>GL Progress</Link>
								</Menu.Item>
								<Menu.Item key="/guild/alliances" icon={<FontAwesomeIcon icon={faRebel} className={styles.icon}/>} className={styles.item}>
									<Link to="/guild/alliances" className={styles.link}>Alliances</Link>
								</Menu.Item>
							</SubMenu>
						</React.Fragment>}

                        <SubMenu disabled={locked} key="/template" title="Template" icon={<FontAwesomeIcon icon={faUserEdit} className={styles.icon}/>} className={styles.category}>
                            <Menu.Item key="/template/editor"
                                       icon={<FontAwesomeIcon icon={faUserEdit} className={styles.icon}/>} className={styles.item}>
                                <Link to="/template/editor" className={styles.link}>Editor</Link>
                            </Menu.Item>


                            <Menu.Item key="/template/squadcompliance"
                                       icon={<FontAwesomeIcon icon={faUserPolice} className={styles.icon}/>} className={styles.item}>
                                <Link to="/template/squadcompliance" className={styles.link}>Squad Editor</Link>
                            </Menu.Item>

                            <Menu.Item key="/template/execute"
                                       icon={<FontAwesomeIcon icon={faPlayCircle} className={styles.icon}/>} className={styles.item}>
                                <Link to="/template/execute" className={styles.link}>Execute</Link>
                            </Menu.Item>
                        </SubMenu>

                        {/* SE: Gear — an EXTERNAL link (swgohevents.com), not a route, which is why it is a
                            bare <a> and why no key of it appears in openKeys(). It was the only child of
                            the old "Other" section, so that section went away with the move. */}
                        <Menu.Item key="/gear" disabled={locked} icon={<FontAwesomeIcon icon={faUserCog} className={styles.icon}/>} className={styles.item}>
                            {showFullEventsLink ? <a href={'https://gear.swgohevents.com/?HotAllyCode=' + this.props.user!.currentPlayer!.allyCode}
                                                     target="_blank" rel="noopener noreferrer">SE: Gear</a> : <a href={'https://gear.swgohevents.com'} target="_blank" rel="noopener noreferrer"> SE:
                                Gear</a>}
                        </Menu.Item>
                    </SubMenu>
                </Menu.ItemGroup>

            </Menu>);
    }
}

export default withRouter(GlobalMenu);
